import type { DataProvider } from "ra-core";
import type { ImpersonationSession } from "@slate/shared";
import { USER_STORAGE_KEY } from "../providers/fakerest/authProvider";

/**
 * Support-session plumbing shared by the tenant app (banner, Settings audit
 * card, read-only guard) and the operator console. Lives on the tenant side
 * because tenants SEE support sessions (docs/decisions.md #15); it carries no
 * console routes or strings.
 *
 * Demo storage is localStorage so a session survives reloads even though the
 * demo data store is in-memory.
 */

export const SUPPORT_SESSION_KEY = "slate.supportSession";
export const OPERATOR_STASH_KEY = "slate.operatorStash";
export const SUPPORT_LOG_KEY = "slate.supportSessionLog";
export const SUPPORT_SESSION_EVENT = "slate:support-session";

export interface ActiveSupportSession extends ImpersonationSession {
  /** Hash to return the operator to when the session ends. */
  returnTo: string;
}

const notify = () => window.dispatchEvent(new Event(SUPPORT_SESSION_EVENT));

export function getActiveSupportSession(): ActiveSupportSession | null {
  try {
    const raw = localStorage.getItem(SUPPORT_SESSION_KEY);
    return raw ? (JSON.parse(raw) as ActiveSupportSession) : null;
  } catch {
    return null;
  }
}

export function getSupportSessionLog(): ImpersonationSession[] {
  try {
    const raw = localStorage.getItem(SUPPORT_LOG_KEY);
    return raw ? (JSON.parse(raw) as ImpersonationSession[]) : [];
  } catch {
    return [];
  }
}

/**
 * Swap the demo auth identity to the target user, stashing the operator's
 * identity for restoration. The target's OWN identity is used - permissions
 * apply unchanged (decision #15).
 */
export function beginSupportSession(
  session: ActiveSupportSession,
  targetUserJson: string,
) {
  const current = localStorage.getItem(USER_STORAGE_KEY);
  if (current) localStorage.setItem(OPERATOR_STASH_KEY, current);
  localStorage.setItem(SUPPORT_SESSION_KEY, JSON.stringify(session));
  localStorage.setItem(USER_STORAGE_KEY, targetUserJson);
  notify();
}

/** End the active session (manual or TTL expiry) and restore the operator. */
export function endSupportSession(): ActiveSupportSession | null {
  const session = getActiveSupportSession();
  if (!session) return null;
  const ended: ImpersonationSession = {
    ...session,
    endedAt: new Date().toISOString(),
  };
  const log = getSupportSessionLog();
  localStorage.setItem(SUPPORT_LOG_KEY, JSON.stringify([ended, ...log].slice(0, 50)));
  localStorage.removeItem(SUPPORT_SESSION_KEY);
  const stash = localStorage.getItem(OPERATOR_STASH_KEY);
  if (stash) {
    localStorage.setItem(USER_STORAGE_KEY, stash);
    localStorage.removeItem(OPERATOR_STASH_KEY);
  }
  notify();
  return session;
}

const MUTATIONS = [
  "create",
  "update",
  "updateMany",
  "delete",
  "deleteMany",
] as const;

/**
 * Demo enforcement of read-only support sessions: block every mutation verb
 * while a read_only session is active. Production enforcement moves below
 * the client (Custom Access Token hook + RLS) before write mode ships.
 */
export function guardDataProviderForSupport<T extends DataProvider>(dp: T): T {
  const guarded: Record<string, unknown> = {};
  for (const key of Object.keys(dp)) {
    const fn = (dp as Record<string, unknown>)[key];
    if (
      typeof fn === "function" &&
      (MUTATIONS as readonly string[]).includes(key)
    ) {
      guarded[key] = (...args: unknown[]) => {
        const session = getActiveSupportSession();
        if (session && session.mode === "read_only" && !session.endedAt) {
          return Promise.reject(
            new Error(
              "Read-only support session - changes are disabled. End the session to make edits.",
            ),
          );
        }
        return (fn as (...a: unknown[]) => unknown).apply(dp, args);
      };
    } else {
      guarded[key] = typeof fn === "function" ? fn.bind(dp) : fn;
    }
  }
  return guarded as T;
}
